import { useState, useRef } from "react";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import "./TemplateGenerator.css";

import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Drawer,
  Divider,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import FileTree from "./FileTree";

const TemplateGenerator = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedPdf, setSelectedPdf] = useState("");
  const [selectedHtml, setSelectedHtml] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [jsonInput, setJsonInput] = useState("");
  const [isJsonDialogOpen, setIsJsonDialogOpen] = useState(false);
  const [selectedConvertedPdf, setSelectedConvertedPdf] = useState("");

  const fileInputRef = useRef(null);

  // helper to upload a single file to backend (use templatecorrection endpoint to store image and return URL)
  const uploadFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    // tell backend this is an upload action (backend should handle multipart at this route)
    form.append("action", "upload");
    try {
      const res = await fetch(`${API_BASE_URL}/api/assets/upload`, {
        method: "POST",
        body: form,
      });
      const data = await res.json();
      if (!res.ok || (!data.url && !data.fileUrl)) {
        throw new Error(data.error || "Upload failed");
      }
      return data.url || data.fileUrl;
    } catch (err) {
      console.error("Upload error", err);
      return null;
    }
  };

  const API_BASE_URL =
    process.env.REACT_APP_FASTAPI_URL || "https://docmorph.ivldsp.in";

  const handleFileSelect = (pdfUrl, convertedPdfUrl, convertedHtmlUrl) => {
    setSelectedPdf(pdfUrl);
    setSelectedConvertedPdf(convertedPdfUrl);
    setSelectedHtml(convertedHtmlUrl);
  };

  const handleChatSend = async () => {
    // find pending user message(s) with a file
    const pendingIndex = chatMessages.findIndex(
      (m) => m.sender === "user" && m.file
    );

    let attachmentUrl = null;
    if (pendingIndex !== -1) {
      const pending = chatMessages[pendingIndex];
      // upload first attached file USING templatecorrection endpoint
      const url = await uploadFile(pending.file);
      if (!url) {
        alert("Failed to upload attachment.");
        return;
      }
      attachmentUrl = url;
      // replace the pending message file with file URL
      setChatMessages((prev) => {
        const copy = [...prev];
        copy[pendingIndex] = {
          sender: "user",
          text: `📎 ${pending.file.name}`,
          fileUrl: url,
        };
        return copy;
      });
    }

    // existing send logic (API calls, adding AI-loading message etc.)
    if (!chatInput.trim() || !selectedHtml) {
      alert("Please select an HTML template first!");
      return;
    }

    const filename = selectedHtml.split("/").pop();
    setChatMessages((prev) => [...prev, { sender: "user", text: chatInput }]);
    setChatInput("");
    setChatMessages((prev) => [
      ...prev,
      { sender: "ai-loading", text: "Processing" },
    ]);

    try {
      // if we uploaded an attachment, append its URL into the prompt so backend can use it
      const promptToSend = attachmentUrl
        ? `${chatInput}\n\nAttachedImageURL: ${attachmentUrl}`
        : chatInput;

      const res = await fetch(`${API_BASE_URL}/api/ai/templatecorrection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: promptToSend, filename }),
      });

      const data = await res.json();

      // 🔽 REMOVE loader *AFTER* response
      setChatMessages((prev) =>
        prev.filter((msg) => msg.sender !== "ai-loading")
      );

      if (data.error) throw new Error(data.error);

      setChatMessages((prev) => [
        ...prev,
        { sender: "ai", text: "✅ Template refined successfully!" },
      ]);

      const cache = `?t=${Date.now()}`;
      setSelectedHtml(`/${filename}${cache}`);
      setSelectedConvertedPdf((prev) => prev + cache);
    } catch (err) {
      console.error(err);

      // 🔽 Replace loader with error message
      setChatMessages((prev) =>
        prev.map((msg) =>
          msg.sender === "ai-loading"
            ? { sender: "ai", text: "⚠️ Error refining the template." }
            : msg
        )
      );
    }
  };
  const handleEditTemplate = async () => {
    try {
      // Ask user to select a new PDF file
      const fileInput = document.createElement("input");
      fileInput.type = "file";
      fileInput.accept = "application/pdf";

      fileInput.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!selectedHtml) {
          alert("Please select a template to update first!");
          return;
        }

        const template_id = selectedHtml.split("/").pop();

        const formData = new FormData();
        formData.append("file", file);
        formData.append("template_id", template_id);

        try {
          const res = await fetch(`${API_BASE_URL}/api/ai/edittemplate`, {
            method: "POST",
            body: formData,
          });

          const data = await res.json();

          if (!data.success) {
            alert(`Error: ${data.error || "Failed to update template"}`);
            return;
          }

          alert("✅ Template updated successfully!");

          // Force reload of updated HTML
          const cacheBuster = `?t=${Date.now()}`;
          setSelectedHtml(`/${template_id}${cacheBuster}`);
        } catch (error) {
          console.error("Error updating template:", error);
          alert("⚠️ Failed to update template. Check console for details.");
        }
      };

      fileInput.click(); // Open file picker
    } catch (err) {
      console.error("Error handling edit template:", err);
      alert("Unexpected error occurred.");
    }
  };

  const handleGeneratePdf = async () => {
    if (!selectedHtml) {
      alert("Please select a template first!");
      return;
    }

    const template_id = selectedHtml.split("/").pop();

    let jsonData;
    try {
      jsonData = JSON.parse(jsonInput || "{}");
    } catch (error) {
      alert("Invalid JSON format!");
      return;
    }

    try {
      setIsJsonDialogOpen(false);
      const res = await fetch(`${API_BASE_URL}/api/ai/runtime`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jsonData, template_id }),
      });

      const data = await res.json();

      if (!data.success) {
        alert(`Error: ${data.error || "Failed to generate PDF"}`);
        return;
      }

      alert("Template generated successfully!");
      setSelectedHtml(
        `${API_BASE_URL}/templates/${data.template_id}?t=${Date.now()}`
      );
    } catch (error) {
      console.error("Error generating template:", error);
      alert("⚠️ Failed to generate template. Check console for details.");
    }
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleAttachChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      alert("Please attach an image file (png/jpg/svg).");
      e.target.value = null;
      return;
    }
    // add pending attachment message (no upload yet)
    setChatMessages((prev) => [
      ...prev,
      { sender: "user", text: `📎 ${file.name}`, file },
    ]);
    e.target.value = null;
  };

  // compute if there's any pending attached file to allow Send when chatInput empty
  const hasPendingFile = chatMessages.some((m) => m.sender === "user" && m.file);

  return (
    <Box
      sx={{
        display: "flex",
        height: "95vh",
        gap: 2,
        p: 2,
        backgroundColor: "#f7f9fc",
        overflow: "hidden",
      }}
    >
      {/* 📁 Collapsible Sidebar */}
      <Collapse in={isSidebarOpen} orientation="horizontal">
        <Paper
          elevation={3}
          sx={{
            width: 280,
            p: 2,
            borderRadius: 3,
            overflowY: "auto",
            transition: "width 0.3s ease",
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Uploaded PDFs
          </Typography>
          <FileTree onFileSelect={handleFileSelect} />
        </Paper>
      </Collapse>

      {/* Toggle Button for Sidebar */}
      <IconButton
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        sx={{
          position: "absolute",
          top: 100,
          left: isSidebarOpen ? 300 : 10,
          zIndex: 10,
          transition: "left 0.3s ease",
          background: "#fff",
          boxShadow: 1,
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* 🧾 Comparison Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          flexGrow: 1,
          gap: 2,
          height: "100%",
        }}
      >
        {/* Original PDF */}
        <Paper
          elevation={3}
          sx={{ p: 2, display: "flex", flexDirection: "column" }}
        >
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Original PDF
          </Typography>
          {selectedPdf ? (
            <iframe
              src={selectedPdf}
              title="PDF Viewer"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                flexGrow: 1,
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Select a PDF from the sidebar
            </Typography>
          )}
        </Paper>
        {/* Derived PDF Preview */}
        <Paper
          elevation={3}
          sx={{ p: 2, display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Converted PDF Preview</Typography>
            </Box>

            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={handleEditTemplate}
            >
              Edit Template
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              onClick={() => setIsJsonDialogOpen(true)}
            >
              Generate PDF
            </Button>
          </Box>
          {selectedConvertedPdf ? (
            <iframe
              src={selectedConvertedPdf}
              title="Converted PDF Viewer"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              Select a PDF to view its converted version
            </Typography>
          )}
        </Paper>

        {/* Derived Template */}
      </Box>

      {/* 💬 Chat Icon */}
      <IconButton
        color="primary"
        onClick={() => setIsChatOpen(true)}
        sx={{
          zIndex: 10,
          position: "fixed",
          bottom: 20,
          right: 20,
          backgroundColor: "#1976d2",
          color: "#fff",
          "&:hover": { backgroundColor: "#115293" },
        }}
      >
        <ChatIcon />
      </IconButton>

      {/* 🧠 Chat Drawer */}
      <Drawer
        anchor="right"
        open={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        PaperProps={{ sx: { width: 380, p: 2 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6">DocMorph AI Assistant</Typography>
          <IconButton onClick={() => setIsChatOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 2 }} />

        {/* Chat Messages */}
        <Box sx={{ flex: 1, overflowY: "auto", mb: 2, maxHeight: "70vh" }}>
          {chatMessages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 1.5,
                textAlign:
                  msg.sender === "user"
                    ? "right"
                    : msg.sender === "ai-loading"
                    ? "left"
                    : "left",
              }}
            >
              <Paper
                sx={{
                  display: "inline-block",
                  p: 1.5,
                  backgroundColor:
                    msg.sender === "user"
                      ? "#1976d2"
                      : msg.sender === "ai-loading"
                      ? "#e0e0e0"
                      : "#f0f0f0",
                  color:
                    msg.sender === "user"
                      ? "white"
                      : msg.sender === "ai-loading"
                      ? "#555"
                      : "black",
                  borderRadius: 2,
                  maxWidth: "85%",
                  fontStyle: msg.sender === "ai-loading" ? "italic" : "normal",
                }}
              >
                {msg.sender === "ai-loading" ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "4px",
                        alignItems: "center",
                      }}
                    >
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Processing
                    </Typography>
                  </Box>
                ) : (
                  msg.text
                )}
              </Paper>
            </Box>
          ))}
        </Box>

        {/* Chat Input */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <input
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            ref={fileInputRef}
            onChange={handleAttachChange}
          />
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Ask something..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
          />
          <IconButton onClick={handleAttachClick} color="primary">
            <AttachFileIcon />
          </IconButton>
          <Button
            variant="contained"
            onClick={handleChatSend}
            disabled={!chatInput && !hasPendingFile}
          >
            Send
          </Button>
        </Box>
      </Drawer>
      <Dialog
        open={isJsonDialogOpen}
        onClose={() => setIsJsonDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Enter JSON Data</DialogTitle>
        <DialogContent>
          <TextField
            label="JSON Data"
            multiline
            minRows={8}
            placeholder='{"name": "John", "invoice_no": "1234"}'
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsJsonDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            // onClick={() => {
            //   try {
            //     JSON.parse(jsonInput);
            //     setIsJsonDialogOpen(false);
            //     alert("✅ JSON data saved successfully!");
            //   } catch {
            //     alert("⚠️ Invalid JSON format!");
            //   }
            // }}
            onClick={handleGeneratePdf}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateGenerator;
